import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  Key,
  Smartphone,
  Trash2,
  Download,
  Loader2,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Server,
  ShieldCheck,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { useApp } from '../context';
import { authApi, ApiError } from '../lib/api';
import { showToast } from '../lib/toast';

export default function PrivacySecurity() {
  const navigate = useNavigate();
  const { state, logout } = useApp();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleDeleteAccount = async () => {
    if (state.isGuestMode) {
      logout();
      return;
    }
    if (!deletePassword) {
      setDeleteError('Password is required');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await authApi.deleteAccount(deletePassword);
      logout();
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message : 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async () => {
    const email = state.user?.email;
    if (!email) return;
    setSendingReset(true);
    try {
      await authApi.forgotPassword(email);
      setResetSent(true);
      showToast('Reset link sent to your email', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Failed to send reset email');
    } finally {
      setSendingReset(false);
    }
  };

  const handleExportData = async () => {
    try {
      const data = await authApi.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nudge-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to export data');
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center p-4 pb-2 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">Privacy & Security</h1>
        <div className="size-10" />
      </div>

      <div className="px-5 pb-8 space-y-5 mt-2">
        {/* Account Security */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Account Security
          </h2>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100 overflow-hidden">
            {/* Reset Password */}
            {!state.isGuestMode && (
              <button
                onClick={handleResetPassword}
                disabled={sendingReset || resetSent}
                className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left disabled:opacity-60"
              >
                <div className="size-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  {sendingReset ? (
                    <Loader2 size={20} className="text-blue-500 animate-spin" />
                  ) : resetSent ? (
                    <Mail size={20} className="text-blue-500" />
                  ) : (
                    <Key size={20} className="text-blue-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">
                    {resetSent ? 'Reset Link Sent' : 'Reset Password'}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                    {resetSent
                      ? `Check ${state.user?.email} for the reset link`
                      : "We'll send a reset link to your email"}
                  </p>
                </div>
              </button>
            )}

            {/* 2FA — Coming Soon */}
            <div className="w-full flex items-center gap-4 p-4 opacity-50 cursor-default">
              <div className="size-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                <Smartphone size={20} className="text-purple-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">Two-Factor Authentication</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  Add an extra layer of security
                </p>
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-2.5 py-1 rounded-full shrink-0">
                Soon
              </span>
            </div>
          </div>
        </div>

        {/* Data Encryption */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Data Encryption
          </h2>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-slate-100">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <ShieldCheck size={20} className="text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">AES-256 Encrypted</p>
                  <p className="text-[11px] text-emerald-600 font-semibold mt-0.5">
                    Military-grade encryption active
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Your personal data is encrypted with AES-256-GCM before it reaches our database.
                Even with direct database access, your data appears as unreadable ciphertext.
              </p>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <Lock size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-slate-700">Contact names & phone numbers</p>
                  <p className="text-[11px] text-slate-400">Encrypted at rest in database</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Lock size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-slate-700">Interaction notes</p>
                  <p className="text-[11px] text-slate-400">Encrypted at rest in database</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Lock size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-slate-700">Personal notes on contacts</p>
                  <p className="text-[11px] text-slate-400">Encrypted at rest in database</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Server size={16} className="text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-slate-700">HTTPS in transit</p>
                  <p className="text-[11px] text-slate-400">TLS encryption for all API calls</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Commitment */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Our Privacy Promise
          </h2>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-slate-100">
            <div className="flex items-start gap-3 p-4">
              <EyeOff size={16} className="text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-700">We never read your data</p>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  There is no admin interface to view your contacts, notes, or conversations. Your
                  data is processed by servers only to calculate scores and send reminders.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4">
              <Eye size={16} className="text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-700">No ads, no tracking, no selling</p>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  We don't use third-party analytics, we don't show ads, and we will never sell your
                  data to anyone.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4">
              <Shield size={16} className="text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-700">Your data is yours</p>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  Export all your data anytime. Delete your account and everything is permanently
                  removed within 30 days.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Legal */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Legal
          </h2>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden divide-y divide-slate-100">
            <button
              onClick={() => navigate('/privacy-policy')}
              className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="size-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">Privacy Policy</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  How we handle your data
                </p>
              </div>
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </button>
            <button
              onClick={() => navigate('/terms-of-service')}
              className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left"
            >
              <div className="size-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                <FileText size={20} className="text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-800">Terms of Service</p>
                <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                  Rules for using Nudge
                </p>
              </div>
              <ChevronRight size={18} className="text-slate-300 shrink-0" />
            </button>
          </div>
        </div>

        {/* Data Export */}
        {!state.isGuestMode && (
          <div>
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
              Your Data
            </h2>
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button
                onClick={handleExportData}
                className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left"
              >
                <div className="size-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                  <Download size={20} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">Export My Data</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed">
                    Download all your data as JSON
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div>
          <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 mb-3">
            Danger Zone
          </h2>
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors text-left group"
            >
              <div className="size-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 size={20} className="text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-red-600">Delete Account</p>
                <p className="text-[11px] text-red-400/80 mt-0.5 leading-relaxed">
                  Permanently remove your data
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
              <h3 className="text-lg font-bold text-slate-900">Delete Account?</h3>
              <p className="text-sm text-slate-500">
                This will schedule your account for deletion in 30 days. All your data will be
                permanently removed.
              </p>
              {!state.isGuestMode && (
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password to confirm"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 p-3 text-slate-900 font-medium focus:ring-2 focus:ring-red-300 outline-none"
                />
              )}
              {deleteError && <p className="text-sm text-red-500 font-medium">{deleteError}</p>}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletePassword('');
                    setDeleteError('');
                  }}
                  className="flex-1 py-3 rounded-xl border border-slate-200 font-bold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
